import {
  COLOR_NAMES,
  CREATABLE_FIELD_TYPES,
  FIELD_TYPES,
  formatNumber,
  formulaValue,
  FUNCTIONS,
  parseFormula,
  toDisplayExpression,
  toStoredExpression,
  uid,
  type CollectionData,
  type ColorName,
  type FieldConfig,
  type FieldDef,
  type FieldType,
  type SelectOption,
} from '@tomo/core';
import {
  Button,
  ColorDot,
  ColorSwatches,
  Dialog,
  Field,
  IconButton,
  Input,
  Popover,
  Select,
  Switch,
  Textarea,
  useConfirm,
  type PaletteColor,
} from '@tomo/ui';
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useCollections, useProject, useRecords } from '../../data/hooks.tsx';
import { FieldTypeIcon } from './display.tsx';

/* ---------------------------------------------------------------------------------------------- *
 * Add field: name + type grid
 * ---------------------------------------------------------------------------------------------- */

const NEEDS_SETUP: ReadonlySet<FieldType> = new Set(['select', 'multiSelect', 'relation', 'formula']);

export function AddFieldPopover({
  collection,
  trigger,
  after,
  onCreated,
}: {
  collection: CollectionData;
  trigger: ReactElement;
  after?: string | null;
  onCreated?: (fieldId: string, needsSetup: boolean) => void;
}) {
  const project = useProject();
  const [name, setName] = useState('');
  const types = CREATABLE_FIELD_TYPES.filter((t) => t !== 'votes' || !collection.fields.some((f) => f.type === 'votes'));
  return (
    <Popover trigger={trigger} width={340} padding="md" placement="bottom-end">
      {({ close }) => (
        <div className="add-field">
          <Input size="sm" autoFocus placeholder="Field name" value={name} onChange={(e) => setName(e.target.value)} />
          <div className="g-overline">Type</div>
          <div className="add-field__grid">
            {types.map((t) => (
              <button
                key={t}
                type="button"
                className="add-field__type"
                title={FIELD_TYPES[t].description}
                onClick={() => {
                  const id = project.addField(collection.id, { name: name.trim() || FIELD_TYPES[t].label, type: t, ...(after !== undefined ? { after } : {}) });
                  setName('');
                  close();
                  onCreated?.(id, NEEDS_SETUP.has(t));
                }}
              >
                <FieldTypeIcon type={t} />
                <span>{FIELD_TYPES[t].label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </Popover>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * Edit field dialog
 * ---------------------------------------------------------------------------------------------- */

export function FieldSettingsDialog({ collection, fieldId, onClose }: { collection: CollectionData; fieldId: string | null; onClose: () => void }) {
  const field = collection.fields.find((f) => f.id === fieldId);
  return (
    <Dialog open={!!field} onOpenChange={(o) => !o && onClose()} title="Edit field" size="md" description={field ? FIELD_TYPES[field.type].description : undefined}>
      {field && <FieldSettingsBody key={field.id} collection={collection} field={field} onClose={onClose} />}
    </Dialog>
  );
}

function FieldSettingsBody({ collection, field, onClose }: { collection: CollectionData; field: FieldDef; onClose: () => void }) {
  const project = useProject();
  const confirm = useConfirm();
  const [name, setName] = useState(field.name);
  const update = (patch: Partial<Omit<FieldDef, 'id'>>) => project.updateField(collection.id, field.id, patch);
  const updateConfig = (patch: Partial<FieldConfig>) => update({ config: { ...field.config, ...patch } });
  const typeOptions = (Object.keys(FIELD_TYPES) as FieldType[])
    .filter((t) => FIELD_TYPES[t].creatable || t === field.type)
    .map((t) => ({ value: t, label: FIELD_TYPES[t].label, icon: <FieldTypeIcon type={t} /> }));

  return (
    <div className="field-settings">
      <div className="field-settings__row">
        <Field label="Name" htmlFor="fs-name" className="field-settings__grow">
          <Input
            id="fs-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => name.trim() && name.trim() !== field.name && update({ name: name.trim() })}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          />
        </Field>
        <Field label="Type">
          <Select
            value={field.type}
            options={typeOptions}
            minWidth={200}
            onChange={async (t) => {
              if (t === field.type) return;
              const ok = await confirm({
                title: `Change “${field.name}” to ${FIELD_TYPES[t].label}?`,
                description: 'Existing values are converted where possible; values that don’t fit the new type are cleared.',
                confirmLabel: 'Change type',
              });
              if (ok) update({ type: t });
            }}
          />
        </Field>
      </div>

      <Field label="Description" htmlFor="fs-desc" hint="Shown as a tooltip on the column header.">
        <Textarea id="fs-desc" rows={2} autoGrow defaultValue={field.description ?? ''} onBlur={(e) => e.target.value !== (field.description ?? '') && update({ description: e.target.value })} />
      </Field>

      {(field.type === 'number' || field.type === 'range') && <NumberSettings field={field} onChange={updateConfig} />}
      {(field.type === 'select' || field.type === 'multiSelect') && <OptionsEditor options={field.config.options ?? []} onChange={(options) => updateConfig({ options })} />}
      {field.type === 'relation' && <RelationSettings field={field} collection={collection} onChange={updateConfig} />}
      {field.type === 'person' && (
        <Switch label="Allow several people" checked={field.config.multiple !== false} onCheckedChange={(multiple) => updateConfig({ multiple })} />
      )}
      {field.type === 'rating' && (
        <Field label="Maximum stars">
          <Select value={String(field.config.max ?? 5)} onChange={(v) => updateConfig({ max: Number(v) })} options={['3', '4', '5', '6', '7', '8', '10'].map((v) => ({ value: v, label: v }))} />
        </Field>
      )}
      {field.type === 'votes' && (
        <Switch
          label="Allow downvotes"
          description="Off by default — upvote-only keeps brainstorming positive."
          checked={!!field.config.allowDownvotes}
          onCheckedChange={(allowDownvotes) => updateConfig({ allowDownvotes })}
        />
      )}
      {field.type === 'formula' && <FormulaEditor field={field} collection={collection} onChange={updateConfig} />}

      <div className="field-settings__footer">
        <Button
          variant="danger-soft"
          icon={<Trash2 />}
          onClick={async () => {
            const ok = await confirm({
              title: `Delete “${field.name}”?`,
              description: 'Its values are removed from every record. You can undo right after with Ctrl+Z.',
              confirmLabel: 'Delete field',
              danger: true,
            });
            if (ok) {
              project.removeField(collection.id, field.id);
              onClose();
            }
          }}
        >
          Delete field
        </Button>
        <Button variant="primary" onClick={onClose} style={{ marginLeft: 'auto' }}>
          Done
        </Button>
      </div>
    </div>
  );
}

function NumberSettings({ field, onChange }: { field: FieldDef; onChange: (patch: Partial<FieldConfig>) => void }) {
  const c = field.config;
  const num = (s: string) => (s.trim() === '' ? undefined : Number(s));
  return (
    <div className="field-settings__group">
      <div className="field-settings__row">
        <Field label="Prefix">
          <Input size="sm" placeholder="e.g. $" defaultValue={c.prefix ?? ''} onBlur={(e) => onChange({ prefix: e.target.value || undefined })} />
        </Field>
        <Field label="Suffix">
          <Input size="sm" placeholder="e.g. s, g, %" defaultValue={c.suffix ?? ''} onBlur={(e) => onChange({ suffix: e.target.value || undefined })} />
        </Field>
        <Field label="Decimals">
          <Select
            size="sm"
            value={c.decimals === undefined ? 'auto' : String(c.decimals)}
            onChange={(v) => onChange({ decimals: v === 'auto' ? undefined : Number(v) })}
            options={[{ value: 'auto', label: 'Auto' }, ...['0', '1', '2', '3'].map((v) => ({ value: v, label: v }))]}
          />
        </Field>
      </div>
      {field.type === 'number' && (
        <>
          <Switch
            label="Show as stat bar"
            description="Draw values as a bar from min to max — great for HP, cost or speed."
            checked={c.display === 'bar'}
            onCheckedChange={(on) => onChange({ display: on ? 'bar' : 'number', max: c.max ?? 100, min: c.min ?? 0 })}
          />
          {c.display === 'bar' && (
            <div className="field-settings__row">
              <Field label="Min">
                <Input size="sm" inputMode="decimal" defaultValue={c.min ?? 0} onBlur={(e) => onChange({ min: num(e.target.value) })} />
              </Field>
              <Field label="Max">
                <Input size="sm" inputMode="decimal" defaultValue={c.max ?? 100} onBlur={(e) => onChange({ max: num(e.target.value) })} />
              </Field>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function OptionsEditor({ options, onChange }: { options: SelectOption[]; onChange: (options: SelectOption[]) => void }) {
  const [draft, setDraft] = useState('');
  const move = (i: number, d: -1 | 1) => {
    const next = [...options];
    const [x] = next.splice(i, 1);
    next.splice(i + d, 0, x!);
    onChange(next);
  };
  const add = () => {
    const label = draft.trim();
    if (!label) return;
    const color = (['blue', 'green', 'orange', 'purple', 'pink', 'teal', 'yellow', 'red', 'brown', 'gray'] as ColorName[])[options.length % 10]!;
    onChange([...options, { id: uid(8), label, color }]);
    setDraft('');
  };
  return (
    <div className="field-settings__group">
      <div className="g-overline">Options</div>
      <div className="options-editor">
        {options.map((o, i) => (
          <div key={o.id} className="options-editor__row">
            <Popover
              trigger={
                <button type="button" className="options-editor__color" aria-label="Change color">
                  <ColorDot color={o.color} size={12} />
                </button>
              }
              padding="md"
              width={236}
            >
              {({ close }) => (
                <ColorSwatches
                  value={o.color}
                  colors={COLOR_NAMES as unknown as PaletteColor[]}
                  onChange={(color) => {
                    onChange(options.map((x) => (x.id === o.id ? { ...x, color } : x)));
                    close();
                  }}
                />
              )}
            </Popover>
            <Input
              size="sm"
              variant="ghost"
              defaultValue={o.label}
              onBlur={(e) => e.target.value.trim() && e.target.value !== o.label && onChange(options.map((x) => (x.id === o.id ? { ...x, label: e.target.value.trim() } : x)))}
              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
              wrapperClassName="options-editor__label"
            />
            <IconButton label="Move up" size="xs" disabled={i === 0} onClick={() => move(i, -1)} tooltip={false}>
              <ArrowUp />
            </IconButton>
            <IconButton label="Move down" size="xs" disabled={i === options.length - 1} onClick={() => move(i, 1)} tooltip={false}>
              <ArrowDown />
            </IconButton>
            <IconButton label="Remove option" size="xs" onClick={() => onChange(options.filter((x) => x.id !== o.id))} tooltip={false}>
              <X />
            </IconButton>
          </div>
        ))}
        <form
          className="options-editor__add"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <Input size="sm" placeholder="Add an option…" value={draft} onChange={(e) => setDraft(e.target.value)} leading={<Plus />} />
        </form>
      </div>
    </div>
  );
}

function RelationSettings({ field, collection, onChange }: { field: FieldDef; collection: CollectionData; onChange: (patch: Partial<FieldConfig>) => void }) {
  const collections = useCollections();
  return (
    <div className="field-settings__group">
      <Field label="Links to" hint="Restricting to one collection makes the picker faster and enables thumbnails for asset libraries.">
        <Select
          value={field.config.targetCollectionId ?? '__any'}
          onChange={(v) => onChange({ targetCollectionId: v === '__any' ? null : v })}
          options={[{ value: '__any', label: 'Any collection' }, ...collections.filter((c) => c.id !== collection.id || true).map((c) => ({ value: c.id, label: c.name }))]}
        />
      </Field>
      <Switch label="Allow several links" checked={field.config.multiple !== false} onCheckedChange={(multiple) => onChange({ multiple })} />
    </div>
  );
}

function FormulaEditor({ field, collection, onChange }: { field: FieldDef; collection: CollectionData; onChange: (patch: Partial<FieldConfig>) => void }) {
  const project = useProject();
  const records = useRecords(collection.id);
  const others = collection.fields.filter((f) => f.id !== field.id && FIELD_TYPES[f.type].numeric);
  const [text, setText] = useState(() => toDisplayExpression(field.config.expression ?? '', collection.fields));
  useEffect(() => setText(toDisplayExpression(field.config.expression ?? '', collection.fields)), [field.config.expression, collection.fields]);

  const { expression, unknown } = useMemo(() => toStoredExpression(text, collection.fields), [text, collection.fields]);
  const error = useMemo(() => {
    if (!text.trim()) return null;
    if (unknown.length) return `Unknown field${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}`;
    try {
      parseFormula(expression);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : String(e);
    }
  }, [text, expression, unknown]);

  const preview = useMemo(() => {
    if (error || !text.trim()) return [];
    const probe = { ...field, config: { ...field.config, expression } };
    return records.slice(0, 3).map((r) => ({ title: r.title || 'Untitled', value: formulaValue(r, probe, { ...project.valueContext(), fields: collection.fields }) }));
  }, [error, text, records, field, expression, project, collection.fields]);

  const save = () => {
    if (!error) onChange({ expression });
  };

  return (
    <div className="field-settings__group">
      <Field label="Formula" error={error} hint={!error ? 'Reference fields with {Field name}. Numbers, + − × ÷ ^, comparisons and functions.' : undefined}>
        <Textarea className="g-mono formula-input" rows={2} autoGrow value={text} onChange={(e) => setText(e.target.value)} onBlur={save} spellCheck={false} placeholder="{Damage} * {Attack speed}" />
      </Field>
      <div className="formula-chips">
        {others.map((f) => (
          <button key={f.id} type="button" className="formula-chip" onClick={() => setText((t) => `${t}${t && !t.endsWith(' ') ? ' ' : ''}{${f.name}}`)}>
            <FieldTypeIcon type={f.type} size={12} />
            {f.name}
          </button>
        ))}
      </div>
      <details className="formula-help">
        <summary>Functions</summary>
        <ul>
          {Object.values(FUNCTIONS).map((fn) => (
            <li key={fn.help}>
              <code>{fn.help}</code>
            </li>
          ))}
        </ul>
      </details>
      {preview.length > 0 && (
        <div className="formula-preview">
          {preview.map((p, i) => (
            <div key={i} className="formula-preview__row">
              <span className="g-truncate">{p.title}</span>
              <span className="tabular">{p.value === null ? '—' : formatNumber(p.value, field.config)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="field-settings__row">
        <Field label="Decimals">
          <Select
            size="sm"
            value={field.config.decimals === undefined ? 'auto' : String(field.config.decimals)}
            onChange={(v) => onChange({ decimals: v === 'auto' ? undefined : Number(v) })}
            options={[{ value: 'auto', label: 'Auto' }, ...['0', '1', '2', '3'].map((v) => ({ value: v, label: v }))]}
          />
        </Field>
        <Field label="Suffix">
          <Input size="sm" placeholder="e.g. dps" defaultValue={field.config.suffix ?? ''} onBlur={(e) => onChange({ suffix: e.target.value || undefined })} />
        </Field>
      </div>
      <Button size="sm" variant="secondary" onClick={save} disabled={!!error}>
        Apply formula
      </Button>
    </div>
  );
}
